import { usePartnerId } from 'legacy/lut/classes/helpers/partner360/usePartnerId'
import {
  requestGlobalPartnerData,
  selectGlobalPartnerData,
} from 'legacy/lut/features/globalPartner'
import { requestBasicInfoData } from 'legacy/lut/features/partner360/basicInfo'
import {
  requestCommitmentItems,
  requestCommitmentStatuses,
} from 'legacy/lut/features/partner360/commitment'
import {
  requestPartnerData,
  selectFinancialYear,
  selectProgramType,
} from 'legacy/lut/features/partner360/configuration'
import { requestPartnerMarketLeasing } from 'legacy/lut/features/partner360/marketInfo'
import { requestSectionSettings } from 'legacy/lut/features/partner360/sectionSettings/api/requestSectionSettings'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { FC, ReactNode, useEffect } from 'react'
import { useSelector } from 'react-redux'

import MeasureController from 'core/analytics/MeasureController'
import { useMeasureController } from 'core/analytics/useMeasureController'
import { useSyncPartnerActualsType } from 'partner360/components/actualsTypeButtons/hooks/useSyncPartnerActualsType'

interface IGP360ApplicationProps {
  urlPartnerId: string
  widgetParams: IObjectAny
  children: ReactNode
  metrics: MeasureController
}

export const GP360Application: FC<IGP360ApplicationProps> = ({
  urlPartnerId,
  widgetParams,
  children,
  metrics,
}) => {
  const dispatch = useAppDispatch()
  const partnerId = usePartnerId(widgetParams, urlPartnerId) as string
  const fYear = useSelector(selectFinancialYear)
  const programType = useSelector(selectProgramType)
  const globalPartnerData = useSelector(selectGlobalPartnerData)

  const { startLoadData, stopLoadData } = useMeasureController(metrics)

  useSyncPartnerActualsType()

  useEffect(() => {
    startLoadData()

    const parameters = {
      partnerId,
      fYear,
    }

    const asyncThunks = [
      dispatch(requestGlobalPartnerData(parameters)),
      dispatch(requestSectionSettings(parameters)),
      dispatch(requestPartnerData(parameters)),
      dispatch(requestCommitmentStatuses(parameters)),
    ]

    Promise.all(asyncThunks).finally(stopLoadData)

    return () => {
      asyncThunks.forEach((action) => {
        action.abort('Unmount GP360Application')
      })
      stopLoadData()
    }
  }, [dispatch, partnerId, fYear, startLoadData, stopLoadData])

  useEffect(() => {
    if (globalPartnerData) {
      startLoadData()

      const parameters = {
        partnerId,
        fYear,
        programType,
      }

      const asyncThunks = [
        dispatch(requestBasicInfoData(parameters)),
        dispatch(requestPartnerMarketLeasing(parameters)),
        dispatch(requestCommitmentItems(parameters)),
      ]

      Promise.all(asyncThunks).finally(stopLoadData)

      return () => {
        asyncThunks.forEach((action) => {
          action.abort('Unmount GP360Application')
        })
        stopLoadData()
      }
    }

    return
  }, [dispatch, globalPartnerData, partnerId, fYear, programType, startLoadData, stopLoadData])

  return <>{children}</>
}
